import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { forceCenter, forceCollide, forceLink, forceManyBody, forceSimulation } from 'd3-force';
import { trackEvent } from '../services/analytics';
import '../styles/LibraryConstellation.css';

const WIDTH = 900;
const HEIGHT = 680;
const SIMULATION_TICKS = 400;

// A node's drawn radius — kept as a shared function so the collision force can guarantee a real
// gap above it (see below), otherwise nodes visually overlap even when d3 reports "no collision".
const nodeRadius = (total) => 16 + Math.sqrt(total) * 0.62;

// Runs the physics simulation synchronously to a fixed number of ticks and returns the final
// node positions — a one-shot static layout rather than a continuously animating simulation,
// which is simpler to render reliably in React and plenty for a collection map that doesn't
// need to react to live data changes after load.
const layoutGraph = (nodes, edges) => {
  const simNodes = nodes.map((node) => ({ ...node }));
  const simLinks = edges.map((edge) => ({ ...edge }));

  const simulation = forceSimulation(simNodes)
    .force('link', forceLink(simLinks).id((d) => d.key).distance((d) => 260 - Math.min(d.weight, 200)).strength(0.08))
    .force('charge', forceManyBody().strength(-420))
    .force('center', forceCenter(WIDTH / 2, HEIGHT / 2))
    .force('collide', forceCollide((d) => nodeRadius(d.total) + 14))
    .stop();

  for (let i = 0; i < SIMULATION_TICKS; i += 1) {
    simulation.tick();
  }

  return simNodes;
};

function LibraryConstellation() {
  const navigate = useNavigate();
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredKey, setHoveredKey] = useState(null);

  useEffect(() => {
    trackEvent('library_constellation_viewed', {});

    fetch('/api/subjects/graph')
      .then((response) => (response.ok ? response.json() : { nodes: [], edges: [] }))
      .then((data) => {
        setGraph(data);
      })
      .catch(() => setGraph({ nodes: [], edges: [] }))
      .finally(() => setLoading(false));
  }, []);

  const laidOutNodes = useMemo(() => {
    if (!graph || !graph.nodes.length) {
      return [];
    }
    return layoutGraph(graph.nodes, graph.edges || []);
  }, [graph]);

  const nodeByKey = useMemo(() => new Map(laidOutNodes.map((node) => [node.key, node])), [laidOutNodes]);
  const maxWeight = Math.max(...(graph?.edges || []).map((edge) => edge.weight), 1);

  const handleNodeClick = (key) => {
    trackEvent('library_constellation_node_clicked', { subjectKey: key });
    navigate(`/subjects/${key}`);
  };

  if (loading) {
    return <div className="constellation-state">Charting the collection...</div>;
  }

  if (!laidOutNodes.length) {
    return <div className="constellation-state">No subject data available yet.</div>;
  }

  return (
    <section className="library-constellation" aria-label="Library constellation map">
      <div className="library-constellation__header">
        <h1>🌌 Library Constellation</h1>
        <p>Subjects sized by collection depth, connected by how often readers explore them together.</p>
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="library-constellation__svg"
        role="img"
        aria-label="Subject relationship graph"
      >
        {(graph.edges || []).map((edge) => {
          const source = nodeByKey.get(edge.source);
          const target = nodeByKey.get(edge.target);
          if (!source || !target) {
            return null;
          }
          const isHighlighted = hoveredKey && (edge.source === hoveredKey || edge.target === hoveredKey);
          return (
            <line
              key={`${edge.source}-${edge.target}`}
              x1={source.x}
              y1={source.y}
              x2={target.x}
              y2={target.y}
              className={`library-constellation__edge ${isHighlighted ? 'library-constellation__edge--active' : ''}`}
              strokeWidth={1 + (edge.weight / maxWeight) * 4}
            />
          );
        })}
        {laidOutNodes.map((node) => {
          const radius = nodeRadius(node.total);
          return (
            <g
              key={node.key}
              transform={`translate(${node.x}, ${node.y})`}
              className="library-constellation__node"
              onMouseEnter={() => setHoveredKey(node.key)}
              onMouseLeave={() => setHoveredKey(null)}
              onClick={() => handleNodeClick(node.key)}
              tabIndex={0}
              role="button"
              aria-label={`${node.label}, ${node.total} titles`}
              onKeyDown={(event) => event.key === 'Enter' && handleNodeClick(node.key)}
            >
              <circle r={radius} fill={node.accent} opacity={hoveredKey && hoveredKey !== node.key ? 0.35 : 0.85} />
              <text textAnchor="middle" dy="-4" className="library-constellation__icon">{node.icon}</text>
              <text textAnchor="middle" dy={radius + 16} className="library-constellation__label">{node.label}</text>
            </g>
          );
        })}
      </svg>
    </section>
  );
}

export default LibraryConstellation;
